import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../utils';

// Pill Component
export const Pill = ({ 
  children, 
  variant = 'default',
  size = 'md',
  removable = false,
  onRemove,
  className,
  ...props 
}) => {
  const getVariantClasses = (variant) => {
    const variants = {
      default: 'bg-gray-100 text-gray-800',
      primary: 'bg-blue-100 text-blue-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      danger: 'bg-red-100 text-red-800',
      info: 'bg-cyan-100 text-cyan-800',
      purple: 'bg-purple-100 text-purple-800',
      pink: 'bg-pink-100 text-pink-800',
      indigo: 'bg-indigo-100 text-indigo-800'
    };
    return variants[variant] || variants.default;
  };

  const getSizeClasses = (size) => {
    const sizes = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
      lg: 'px-3 py-1.5 text-base'
    };
    return sizes[size] || sizes.md;
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium",
        getVariantClasses(variant),
        getSizeClasses(size),
        removable && "pr-1",
        className
      )}
      {...props}
    >
      <span>{children}</span>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1 h-4 w-4 rounded-full hover:bg-black hover:bg-opacity-10 flex items-center justify-center transition-colors"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

// Status Pill
export const StatusPill = ({ 
  status, 
  size = 'md',
  className,
  ...props 
}) => {
  const getStatusVariant = (status) => {
    const statusMap = {
      active: 'success',
      inactive: 'default',
      pending: 'warning',
      completed: 'success',
      cancelled: 'danger',
      prospect: 'primary',
      archived: 'default',
      draft: 'warning',
      published: 'success',
      rejected: 'danger',
      approved: 'success',
      processing: 'info',
      on_hold: 'warning',
      in_progress: 'primary',
      blocked: 'danger',
      not_started: 'default'
    };
    return statusMap[status] || 'default';
  };

  return (
    <Pill
      variant={getStatusVariant(status)}
      size={size}
      className={className}
      {...props}
    >
      {status?.replace('_', ' ')}
    </Pill>
  );
};

// Priority Pill
export const PriorityPill = ({ 
  priority, 
  size = 'md',
  className,
  ...props 
}) => {
  const getPriorityVariant = (priority) => {
    const priorityMap = {
      low: 'default',
      medium: 'warning',
      high: 'danger',
      urgent: 'danger'
    };
    return priorityMap[priority] || 'default';
  };

  return (
    <Pill
      variant={getPriorityVariant(priority)}
      size={size}
      className={className}
      {...props}
    >
      {priority}
    </Pill>
  );
};

// Tag Pill
export const TagPill = ({ 
  tag, 
  size = 'md',
  removable = false,
  onRemove,
  className,
  ...props 
}) => {
  return (
    <Pill
      variant="primary"
      size={size}
      removable={removable}
      onRemove={onRemove}
      className={className}
      {...props}
    >
      {tag}
    </Pill>
  );
};

// Category Pill
export const CategoryPill = ({ 
  category, 
  size = 'md',
  className,
  ...props 
}) => {
  const getCategoryVariant = (category) => {
    const categoryMap = {
      business: 'primary',
      personal: 'success',
      urgent: 'danger',
      important: 'warning',
      low: 'default',
      high: 'danger',
      medium: 'warning'
    };
    return categoryMap[category] || 'default';
  };

  return (
    <Pill
      variant={getCategoryVariant(category)}
      size={size}
      className={className}
      {...props}
    >
      {category}
    </Pill>
  );
};

// Pill Group
export const PillGroup = ({ 
  children, 
  className,
  spacing = 'sm',
  ...props 
}) => {
  const getSpacingClasses = (spacing) => {
    const spacings = {
      xs: 'space-x-1',
      sm: 'space-x-2',
      md: 'space-x-3',
      lg: 'space-x-4'
    };
    return spacings[spacing] || spacings.sm;
  };

  return (
    <div className={cn("flex flex-wrap items-center", getSpacingClasses(spacing), className)} {...props}>
      {children}
    </div>
  );
};

// Filter Pills
export const FilterPills = ({ 
  filters = [], 
  onRemoveFilter,
  onClearAll,
  className,
  ...props 
}) => {
  if (filters.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)} {...props}>
      {filters.map((filter, index) => (
        <Pill
          key={index}
          variant="primary"
          removable={true}
          onRemove={() => onRemoveFilter?.(filter)}
        >
          {filter.label}: {filter.value}
        </Pill>
      ))}
      {filters.length > 1 && (
        <button
          onClick={onClearAll}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
};

// All components are already exported as named exports above
